import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useParams } from "react-router-dom";
import {
  FaChartLine,
  FaInbox,
  FaAddressBook,
  FaBullhorn,
  FaFileAlt,
  FaCog,
  FaUsers,
  FaRobot,
  FaSlidersH,
  FaKey,
  FaPlug,
  FaCreditCard,
  FaShoppingCart,
  FaChevronDown,
  FaChevronRight,
  FaBars,
  FaSignOutAlt,
} from "react-icons/fa";
import {
  getVisibleBusinessMenu,
  businessPath,
} from "../../config/businessMenu";
import { ASSETS } from "../../config/assets";
import { useTheme } from "../../theme/ThemeContext";

const ICONS = {
  dashboard: FaChartLine,
  "team-inbox": FaInbox,
  contacts: FaAddressBook,
  campaign: FaBullhorn,
  template: FaFileAlt,
  configuration: FaCog,
  users: FaUsers,
  automations: FaRobot,
  settings: FaSlidersH,
  "application-bar": FaKey,
  integration: FaPlug,
  "whatsapp-payments": FaCreditCard,
  ecommerce: FaShoppingCart,
};

const MENU_SECTIONS = [
  { label: "Main", ids: ["dashboard", "team-inbox"] },
  { label: "Manage", ids: ["contacts", "campaign", "users"] },
  { label: "Setup", ids: ["template", "configuration", "automations"] },
  { label: "Apps", ids: ["application-bar", "integration"] },
];

const BusinessSidebar = ({
  collapsed,
  onToggleCollapse,
  onNavClick,
  onLogout,
}) => {
  const { theme } = useTheme();
  const { projectId } = useParams();
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState({});

  const visibleMenu = useMemo(
    () => getVisibleBusinessMenu(projectId),
    [projectId],
  );

  const sections = useMemo(() => {
    const assigned = new Set(MENU_SECTIONS.flatMap((section) => section.ids));
    const grouped = MENU_SECTIONS.map((section) => ({
      label: section.label,
      items: section.ids
        .map((id) => visibleMenu.find((item) => item.id === id))
        .filter(Boolean),
    })).filter((section) => section.items.length > 0);
    const leftover = visibleMenu.filter((item) => !assigned.has(item.id));
    if (leftover.length) {
      grouped.push({ label: "More", items: leftover });
    }
    return grouped;
  }, [visibleMenu]);

  useEffect(() => {
    const next = {};
    visibleMenu.forEach((item) => {
      if (
        item.children?.some((child) =>
          location.pathname.includes(`/${child.path}`),
        )
      ) {
        next[item.id] = true;
      }
    });
    if (Object.keys(next).length) {
      setOpenGroups((prev) => ({ ...prev, ...next }));
    }
  }, [location.pathname, visibleMenu]);

  const toggleGroup = (id) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const isGroupActive = (item) =>
    Boolean(
      item.children?.some((child) =>
        location.pathname.includes(`/${child.path}`),
      ),
    );

  const renderItem = (item) => {
    const Icon = ICONS[item.id] || FaChartLine;

    if (!item.children) {
      return (
        <NavLink
          key={item.id}
          to={businessPath(projectId, item.path)}
          end
          className={({ isActive }) =>
            `biz-nav-item ${
              isActive ||
              (item.id === "dashboard" &&
                location.pathname.includes("/dashboard"))
                ? "biz-nav-item-active"
                : ""
            }`
          }
          title={collapsed ? item.label : undefined}
          onClick={onNavClick}
        >
          <span className="biz-nav-icon-wrap">
            <Icon className="biz-nav-icon" />
          </span>
          {!collapsed && <span className="biz-nav-label">{item.label}</span>}
        </NavLink>
      );
    }

    const isOpen = openGroups[item.id];
    const groupActive = isGroupActive(item);

    return (
      <div key={item.id} className="biz-nav-group">
        <button
          type="button"
          className={`biz-nav-item biz-nav-group-btn ${
            isOpen ? "biz-nav-group-open" : ""
          } ${groupActive ? "biz-nav-group-active" : ""}`}
          onClick={() => {
            if (collapsed) {
              onToggleCollapse();
              setOpenGroups((prev) => ({ ...prev, [item.id]: true }));
              return;
            }
            toggleGroup(item.id);
          }}
          title={collapsed ? item.label : undefined}
        >
          <span className="biz-nav-icon-wrap">
            <Icon className="biz-nav-icon" />
          </span>
          {!collapsed && (
            <>
              <span className="biz-nav-group-label">{item.label}</span>
              {isOpen ? (
                <FaChevronDown className="biz-nav-chevron" />
              ) : (
                <FaChevronRight className="biz-nav-chevron" />
              )}
            </>
          )}
        </button>
        {!collapsed && isOpen && (
          <div className="biz-nav-children">
            {item.children.map((child) => (
              <NavLink
                key={child.path + child.label}
                to={businessPath(projectId, child.path)}
                className={({ isActive }) =>
                  `biz-nav-child ${isActive ? "biz-nav-child-active" : ""}`
                }
                onClick={onNavClick}
              >
                {child.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`biz-sidebar ${collapsed ? "biz-sidebar-collapsed" : ""}`}
    >
      <div className="biz-sidebar-brand">
        <div className="biz-sidebar-brand-lockup">
          {!collapsed && (
            <img
              src={ASSETS.brandLogo}
              alt={ASSETS.brandName}
              className="biz-sidebar-logo"
            />
          )}
        </div>
        <button
          type="button"
          className="biz-sidebar-toggle"
          onClick={onToggleCollapse}
          aria-label={collapsed ? "Expand menu" : "Collapse menu"}
          title={collapsed ? "Expand menu" : "Collapse menu"}
        >
          {collapsed ? <FaChevronRight /> : <FaBars />}
        </button>
      </div>

      <nav className="biz-sidebar-nav">
        {sections.map((section) => (
          <div key={section.label} className="biz-nav-section">
            {!collapsed && (
              <p className="biz-nav-section-label">{section.label}</p>
            )}
            {section.items.map(renderItem)}
          </div>
        ))}
      </nav>

      <div className="biz-nav-footer">
        <button
          type="button"
          className="biz-nav-item biz-nav-logout"
          onClick={() => {
            onNavClick?.();
            onLogout?.();
          }}
          title={collapsed ? "Logout" : undefined}
        >
          <span className="biz-nav-icon-wrap">
            <FaSignOutAlt className="biz-nav-icon" />
          </span>
          {!collapsed && <span className="biz-nav-label">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default BusinessSidebar;
